import { Urls } from "../Urls";
import { IS_PROD } from "../Components/Constants";
import axios from "axios";

const FROM_EMAIL = "connect@logisticsnow.in";

export const INTERNAL_RECIPIENTS = IS_PROD ? [
  "connect@logisticsnow.in",
  "raj@logisticsnow.in",
  "radhika@logisticsnow.in",
  "radhika@lorri.ai",
  "associate@logisticsnow.in",
  "sales@thelogisticsnow.com",
  "sales@lorri.ai",
] : ["tech@thelogisticsnow.com",
  "unnati@thelogisticsnow.com"];

export const DEMO_REQUEST_TO = IS_PROD
  ? "sanket.j@logisticsnow.ai"
  : "tejas@logisticsnow.ai";
export const DEMO_REQUEST_CC = IS_PROD
  ? [
    "raj@logisticsnow.ai",
    "radhika@logisticsnow.ai",
    "anvit@logisticsnow.ai",
    "sales@thelogisticsnow.com",
    "sales@lorri.ai",
  ]
  : ["unnati@logisticsnow.ai"];

export const buildPayload = ({ to, cc = [], subject, content }) => ({
  from_email: FROM_EMAIL,
  user_type: "company",
  subject,
  to_email: to,
  content,
  mail_status: "Pending",
  activity: "Email Request",
  cc,
  type : "superlaunch",
  screen : "lorri.ai"
});

export const sendMail = (payload) => axios.post(Urls.log, payload);

export const sendMailToList = (recipients, { subject, content }) =>
  Promise.all(
    recipients.map((to) => sendMail(buildPayload({ to, subject, content })))
  );

const DEFAULT_TIMEZONE = "Asia/Kolkata";
const DEFAULT_TZ_LABEL = "IST";

export const getVisitorLocation = async () => {
  try {
    const { data } = await axios.get("https://ipapi.co/json/", { timeout: 4000 });
    if (data && data.timezone) {
      return {
        country: data.country_name || "",
        timezone: data.timezone,
        tzAbbreviation: data.utc_offset ? `UTC${data.utc_offset.slice(0, 3)}:${data.utc_offset.slice(3)}` : "",
      };
    }
  } catch (err) {
    console.log("Could not detect visitor location:", err);
  }
  return { country: "", timezone: DEFAULT_TIMEZONE, tzAbbreviation: DEFAULT_TZ_LABEL };
};

export const formatInTimezone = (date = new Date(), timezone = DEFAULT_TIMEZONE, tzLabel = DEFAULT_TZ_LABEL) => {
  const formatted = new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: timezone,
  }).format(date);
  return tzLabel ? `${formatted} ${tzLabel}` : formatted;
};

export const formatIST = (date = new Date()) => formatInTimezone(date, DEFAULT_TIMEZONE, DEFAULT_TZ_LABEL);

export const formatScheduledDateTime = (dateStr, timeStr, location) => {
  if (!dateStr || !timeStr) return "—";
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hour, minute] = timeStr.split(":").map(Number);
  const scheduled = new Date(year, month - 1, day, hour, minute);
  const timezone = location?.timezone || DEFAULT_TIMEZONE;
  const tzLabel = location?.tzAbbreviation || DEFAULT_TZ_LABEL;
  return formatInTimezone(scheduled, timezone, tzLabel);
};

// --- Demo Request: internal team notification ---
export const buildDemoInternalContent = (userInput, location) => {
  const initial = (userInput.name || "?").trim().charAt(0).toUpperCase();
  const submittedAt = location?.timezone
    ? formatInTimezone(new Date(), location.timezone, location.tzAbbreviation)
    : formatIST();
  const scheduledFor = formatScheduledDateTime(userInput.date, userInput.time, location);
  const locationLabel = location?.country ? ` · ${location.country}` : "";
  const row = (label, value, isLast) => `
      <tr>
        <td style="padding: 14px 24px; ${isLast ? "" : "border-bottom: 1px solid #eeeeee;"} width: 38%; color: #8a8a99; font-size: 13px; font-weight: 500; vertical-align: top;">${label}</td>
        <td style="padding: 14px 24px; ${isLast ? "" : "border-bottom: 1px solid #eeeeee;"} font-size: 14px; font-weight: 600; color: #1f1f2e; vertical-align: top;">${value}</td>
      </tr>`;

  return `
<!DOCTYPE html>
<html>
<body style="margin: 0; padding: 0; background-color: #f0f0f5; font-family: 'Segoe UI', Helvetica, Arial, sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f0f0f5; padding: 32px 16px;">
    <tr>
      <td align="center">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width: 600px; width: 100%; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 12px rgba(57, 49, 133, 0.08);">

          <tr>
            <td style="background: linear-gradient(135deg, #393185 0%, #5347b8 100%); padding: 32px 32px 28px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="display: inline-block; background-color: rgba(255,255,255,0.15); color: #ffffff; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; padding: 5px 12px; border-radius: 999px; margin-bottom: 14px;">New Lead</span>
                    <h1 style="color: #ffffff; margin: 10px 0 4px; font-size: 22px; font-weight: 700;">Demo Request Received</h1>
                    <p style="color: rgba(255,255,255,0.75); margin: 0; font-size: 14px;">Someone just requested a LoRRI demo · ${submittedAt}${locationLabel}</p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="padding: 28px 32px 8px;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="width: 48px; height: 48px; border-radius: 50%; background-color: #ecebfa; color: #393185; font-size: 18px; font-weight: 700; text-align: center; vertical-align: middle;" align="center">${initial}</td>
                  <td style="padding-left: 14px;">
                    <div style="font-size: 17px; font-weight: 700; color: #1f1f2e;">${userInput.name || "—"}</div>
                    <div style="font-size: 13px; color: #8a8a99;">${userInput.designation || "—"}${userInput.company_name ? ` · ${userInput.company_name}` : ""}</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="padding: 16px 32px 0;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #ecebfa; border-radius: 10px;">
                <tr>
                  <td style="padding: 14px 20px;">
                    <div style="font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #5347b8;">Demo Scheduled For</div>
                    <div style="font-size: 16px; font-weight: 700; color: #1f1f2e; margin-top: 2px;">${scheduledFor}</div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="padding: 16px 32px 28px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border: 1px solid #eeeeee; border-radius: 10px; overflow: hidden;">
                ${row("Name", userInput.name || "—")}
                ${row("Company", userInput.company_name || "—")}
                ${row("Designation", userInput.designation || "—")}
                ${row("Email", `<a href="mailto:${userInput.email}" style="color: #393185; text-decoration: none;">${userInput.email}</a>`)}
                ${row("Contact Number", userInput.contact_number ? `<a href="tel:${userInput.contact_number}" style="color: #393185; text-decoration: none;">${userInput.contact_number}</a>` : "—")}
                ${row("Heard From", userInput.heardFrom || "—")}
                ${row("Newsletter Opt-in", userInput.check ? "Yes" : "No")}
                ${row("Submitted From", location?.country || "—")}
                ${row("Submitted At", submittedAt, true)}
              </table>
            </td>
          </tr>

          <tr>
            <td style="padding: 0 32px 32px;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="border-radius: 8px; background-color: #393185;">
                    <a href="mailto:${userInput.email}" style="display: inline-block; padding: 12px 24px; font-size: 14px; font-weight: 600; color: #ffffff; text-decoration: none;">Reply to ${userInput.name ? userInput.name.split(" ")[0] : "Lead"}</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <tr>
            <td style="padding: 18px 32px; background-color: #fafafc; border-top: 1px solid #f0f0f5;">
              <p style="font-size: 12px; color: #a3a3b3; margin: 0;">This email was automatically generated from the LoRRI demo request form.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`.trim();
};

// --- Demo Request: user confirmation ---
export const buildDemoUserContent = (userInput) => `
<!DOCTYPE html>
<html>
<body style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background-color: #393185; padding: 20px 24px; border-radius: 6px 6px 0 0;">
    <h2 style="color: #ffffff; margin: 0; font-size: 20px;">Your Demo Request is Confirmed — LoRRI</h2>
  </div>
  <div style="border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 6px 6px; padding: 24px;">
    <p style="font-size: 15px; margin: 0 0 16px;">Hi ${userInput.name || "there"},</p>
    <p style="font-size: 15px; margin: 0 0 16px;">
      Thank you for requesting a demo of <strong>LoRRI</strong>. We've received your details and our team will reach out to you shortly to confirm the meeting.
    </p>
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
      <tr>
        <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; width: 40%; color: #666; font-size: 14px;">Name</td>
        <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; font-size: 14px; font-weight: 600;">${userInput.name || "—"}</td>
      </tr>
      <tr>
        <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; color: #666; font-size: 14px;">Company</td>
        <td style="padding: 10px 0; border-bottom: 1px solid #f0f0f0; font-size: 14px; font-weight: 600;">${userInput.company_name || "—"}</td>
      </tr>
      <tr>
        <td style="padding: 10px 0; color: #666; font-size: 14px;">Email</td>
        <td style="padding: 10px 0; font-size: 14px; font-weight: 600;">${userInput.email}</td>
      </tr>
    </table>
    <p style="font-size: 14px; color: #555; margin: 0;">
      If you have any questions in the meantime, feel free to reach us at
      <a href="mailto:connect@logisticsnow.in" style="color: #393185;">connect@logisticsnow.in</a>.
    </p>
  </div>
  <p style="font-size: 12px; color: #999; margin-top: 16px;">© LoRRI — LogisticsNow</p>
</body>
</html>`.trim();

// --- New ScreenZero User: internal team notification ---
export const buildNewUserInternalContent = ({ email, number }) => `
<h4>Hello,</br></br>
${email} has visited the LoRRI Home!
<div>
  Email ID is: ${email}<br/>
  Phone Number is: ${number}
</div></br>
</br>Thanks</h4>`.trim();

// --- New ScreenZero User: user confirmation ---
export const buildNewUserUserContent = ({ email }) => `
<!DOCTYPE html>
<html>
<body style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
  <div style="background-color: #393185; padding: 20px 24px; border-radius: 6px 6px 0 0;">
    <h2 style="color: #ffffff; margin: 0; font-size: 20px;">Welcome to ScreenZero — LoRRI</h2>
  </div>
  <div style="border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 6px 6px; padding: 24px;">
    <p style="font-size: 15px; margin: 0 0 16px;">Hi,</p>
    <p style="font-size: 15px; margin: 0 0 16px;">
      You're now registered on <strong>LoRRI</strong>. Our team will reach out to you shortly.
    </p>
    <p style="font-size: 14px; color: #555; margin: 0 0 16px;">
      Your registered email: <strong>${email}</strong>
    </p>
    <p style="font-size: 14px; color: #555; margin: 0;">
      For any queries, write to us at
      <a href="mailto:connect@logisticsnow.in" style="color: #393185;">connect@logisticsnow.in</a>.
    </p>
  </div>
  <p style="font-size: 12px; color: #999; margin-top: 16px;">© LoRRI — LogisticsNow</p>
</body>
</html>`.trim();
